import { useState, useCallback } from 'react';

export type UseSectionImageOptions = Readonly<{
  imageUrl?: string;
  onSuccess?: (imageUrl: string) => void;
  onError?: (error: string) => void;
  onRemove?: () => void;
}>;

export type UploadResponse = Readonly<{
  success: boolean;
  imageUrl?: string;
  error?: string;
}>;

export type ValidationResult = Readonly<{
  valid: boolean;
  error?: string;
}>;

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
];

const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];

export const useSectionImage = (options: UseSectionImageOptions) => {
  const [imageUrl, setImageUrl] = useState<string | null>(options.imageUrl ?? null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const validateFile = useCallback((file: File): ValidationResult => {
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `File size exceeds 5MB limit (${(file.size / 1024 / 1024).toFixed(2)}MB)`,
      };
    }

    // Check MIME type
    if (!ALLOWED_TYPES.includes(file.type)) {
      return {
        valid: false,
        error: `Invalid file format. Allowed formats: jpg, png, gif, webp, svg`,
      };
    }

    // Check file extension as additional validation
    const fileName = file.name.toLowerCase();
    const hasValidExtension = ALLOWED_EXTENSIONS.some(ext => fileName.endsWith(ext));
    if (!hasValidExtension) {
      return {
        valid: false,
        error: `Invalid file extension. Allowed: jpg, jpeg, png, gif, webp, svg`,
      };
    }

    return { valid: true };
  }, []);

  const uploadImage = useCallback(async (file: File): Promise<UploadResponse> => {
    setUploadError(null);
    setUploadProgress(0);

    // Validate file
    const validation = validateFile(file);
    if (!validation.valid) {
      setUploadError(validation.error ?? 'Invalid file');
      options.onError?.(validation.error ?? 'Invalid file');
      return {
        success: false,
        error: validation.error,
      };
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('image', file);

      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/uploads/section-image', {
        method: 'POST',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        const error = `Upload failed: ${response.statusText}${errorText ? ` - ${errorText}` : ''}`;
        setUploadError(error);
        options.onError?.(error);
        return {
          success: false,
          error,
        };
      }

      const data = await response.json();
      const newImageUrl = data.url || data.imageUrl;

      setImageUrl(newImageUrl);
      setUploadProgress(100);
      options.onSuccess?.(newImageUrl);

      return {
        success: true,
        imageUrl: newImageUrl,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      setUploadError(errorMessage);
      options.onError?.(errorMessage);
      return {
        success: false,
        error: errorMessage,
      };
    } finally {
      setIsUploading(false);
    }
  }, [options, validateFile]);

  const removeImage = useCallback(() => {
    setImageUrl(null);
    setUploadError(null);
    setUploadProgress(0);
    options.onRemove?.();
  }, [options]);

  const clearError = useCallback(() => {
    setUploadError(null);
  }, []);

  return {
    imageUrl,
    isUploading,
    uploadProgress,
    uploadError,
    uploadImage,
    removeImage,
    validateFile,
    clearError,
  };
};
